import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { RefreshCw, TrendingUp } from 'lucide-react';
import { apiErrorMessage, getPoolSeries } from '../api/abfApi';
import type { ActionFeedbackState } from '../components/ActionFeedback';
import { fmtDate, plural } from '../presentation/format';
import type { PoolSeries, PoolSeriesPoint } from '../types/collateralExplorer';

/**
 * WS2 — the pool over time (Oaktree rows 4.6, 9.5): one point per released tape, read from
 * the snapshots written at release. Utilization is drawn over the base on that tape; the
 * basis chip says whether the base was approved or computed at release.
 */

interface Props {
  facilityId: number;
  currency?: string | null;
  onFeedback: (feedback: ActionFeedbackState) => void;
}

const Card = styled.section`
  padding: 1rem; border: 1px solid rgba(148,163,184,.16); border-radius: 12px; background: rgba(8,16,30,.8);
  h3 { margin: 0; color: #f8fafc; font-size: 15px; }
  p.sub { margin: .3rem 0 0; color: #94a3b8; font-size: 12px; }
  .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 12px; margin-top: .9rem; }
  .chart { padding: .6rem .7rem; border: 1px solid rgba(148,163,184,.12); border-radius: 10px; background: rgba(5,12,24,.4); }
  .chart .label { display: flex; justify-content: space-between; color: #94a8c2; font-size: 11px; text-transform: uppercase; letter-spacing: .05em; }
  .chart .value { margin-top: 2px; color: #f8fafc; font-size: 18px; font-weight: 650; font-variant-numeric: tabular-nums; }
  .chart .value small { margin-left: 6px; color: #94a3b8; font-size: 11px; font-weight: 500; }
  button { display: inline-flex; align-items: center; gap: 6px; min-height: 34px; padding: .35rem .7rem; border: 1px solid rgba(148,163,184,.24); border-radius: 8px; color: #cbd5e1; background: rgba(15,23,42,.6); cursor: pointer; font: inherit; font-size: 13px; }
  table { width: 100%; border-collapse: collapse; margin-top: .9rem; font-size: 12px; font-variant-numeric: tabular-nums; }
  th { text-align: left; padding: .45rem .5rem; color: #7dd3fc; font-size: 11px; font-weight: 650; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid rgba(148,163,184,.2); }
  td { padding: .45rem .5rem; border-top: 1px solid rgba(148,163,184,.1); color: #e2e8f0; }
  th.num, td.num { text-align: right; white-space: nowrap; }
  .boundary { margin-top: .7rem; color: #94a3b8; font-size: 11px; line-height: 1.5; }
`;

const money = (v: unknown) => v === null || v === undefined ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 });
const pct = (v: unknown) => v === null || v === undefined ? '—' : `${Number(v).toFixed(2)}%`;
const label = (iso: string) => iso.slice(2, 7);

interface Metric { key: keyof PoolSeriesPoint; name: string; kind: 'pct' | 'money'; danger?: number }
const METRICS: Metric[] = [
  { key: 'utilization_pct', name: 'Utilization (drawn / base)', kind: 'pct', danger: 100 },
  { key: 'delinquency_30_pct', name: '30+ day delinquency', kind: 'pct' },
  { key: 'delinquency_60_pct', name: '60+ day delinquency', kind: 'pct' },
  { key: 'delinquency_90_pct', name: '90+ day delinquency', kind: 'pct' },
  { key: 'net_loss_rate_pct', name: 'Net loss (period)', kind: 'pct' },
  { key: 'cpr_pct', name: 'CPR (annualised)', kind: 'pct' },
  { key: 'cdr_pct', name: 'CDR (annualised)', kind: 'pct' },
  { key: 'weighted_avg_yield_pct', name: 'Weighted average yield', kind: 'pct' },
];

const PoolTrendCard: React.FC<Props> = ({ facilityId, currency, onFeedback }) => {
  const [series, setSeries] = useState<PoolSeries | null>(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSeries(await getPoolSeries(facilityId));
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The pool series could not be loaded.') });
    } finally {
      setLoading(false);
    }
  }, [facilityId, onFeedback]);
  useEffect(() => { void load(); }, [load]);

  const points = useMemo(() => series?.points ?? [], [series]);
  const latest = points[points.length - 1];
  const balanceData = useMemo(() => points.map(p => ({ date: label(p.as_of_date), drawn: p.drawn_amount, base: p.borrowing_base, commitment: p.commitment_amount })), [points]);

  return (
    <Card aria-label="Pool trend" aria-busy={loading}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div>
          <h3><TrendingUp size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />Pool over time</h3>
          <p className="sub">{latest
            ? `${plural(points.length, 'released tape')} with a snapshot, ${fmtDate(points[0].as_of_date)} to ${fmtDate(latest.as_of_date)}${series && series.releasedTapes > points.length ? ` · ${plural(series.releasedTapes - points.length, 'earlier release')} without a snapshot (admin backfill)` : ''}`
            : loading ? 'Loading…' : 'No snapshot yet. One is written when a tape is released; earlier releases need the admin backfill.'}</p>
        </div>
        <button type="button" onClick={() => void load()} disabled={loading}><RefreshCw size={14} />Refresh</button>
      </div>

      {latest && (
        <div className="grid">
          <div className="chart" style={{ gridColumn: 'span 2' }}>
            <div className="label"><span>Drawn · base · commitment</span><span>{currency || ''}</span></div>
            <div className="value">{money(latest.drawn_amount)}<small>drawn ({String(latest.drawn_authority || '').toLowerCase()}) vs base {money(latest.borrowing_base)} ({latest.basis_label?.toLowerCase()})</small></div>
            <div style={{ height: 110 }} aria-hidden="true"><ResponsiveContainer width="100%" height="100%">
              <LineChart data={balanceData} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
                <XAxis dataKey="date" stroke="#64748b" fontSize={9} /><YAxis hide domain={['auto', 'auto']} />
                <Tooltip formatter={(v: unknown) => money(v)} contentStyle={{ background: '#0b1526', border: '1px solid #2b3b50', fontSize: 12 }} />
                <Line type="monotone" dataKey="commitment" name="Commitment" stroke="#64748b" strokeDasharray="4 3" dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="base" name="Base" stroke="#b39ddb" dot={{ r: 2 }} isAnimationActive={false} />
                <Line type="monotone" dataKey="drawn" name="Drawn" stroke="#7fc4e0" dot={{ r: 2 }} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer></div>
          </div>
          {METRICS.map(m => {
            const data = points.map(p => ({ date: label(p.as_of_date), value: p[m.key] == null ? null : Number(p[m.key]) }));
            const value = latest[m.key];
            const prior = points.length > 1 ? points[points.length - 2][m.key] : null;
            const change = value == null || prior == null ? null : Number(value) - Number(prior);
            return <div className="chart" key={m.key}>
              <div className="label"><span>{m.name}</span></div>
              <div className="value">{m.kind === 'pct' ? pct(value) : money(value)}{change != null && <small>{change >= 0 ? '+' : ''}{change.toFixed(2)} pts vs prior</small>}</div>
              <div style={{ height: 70 }} aria-hidden="true"><ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
                  <XAxis dataKey="date" stroke="#64748b" fontSize={9} /><YAxis hide domain={['auto', 'auto']} />
                  {m.danger != null && <ReferenceLine y={m.danger} stroke="#c9786a" strokeDasharray="3 3" />}
                  <Line type="monotone" dataKey="value" stroke="#7fc4e0" dot={{ r: 2 }} isAnimationActive={false} connectNulls={false} />
                </LineChart>
              </ResponsiveContainer></div>
            </div>;
          })}
        </div>
      )}

      {points.length > 0 && (
        <table>
          <thead><tr><th>Tape as of</th><th>Basis</th><th className="num">Loans</th><th className="num">Collateral</th><th className="num">Eligible</th><th className="num">Base</th><th className="num">Drawn</th><th className="num">Util %</th><th className="num">DQ30</th><th className="num">DQ60</th><th className="num">DQ90</th><th className="num">Net loss</th><th className="num">CPR</th><th className="num">CDR</th><th className="num">WA yield</th><th className="num">Excess spread</th></tr></thead>
          <tbody>{[...points].reverse().map(p => (
            <tr key={p.id}>
              <td>{fmtDate(p.as_of_date)}</td><td style={{ color: '#94a3b8' }}>{p.basis_label?.toLowerCase() || '—'}</td>
              <td className="num">{money(p.asset_count)}</td><td className="num">{money(p.collateral_balance)}</td><td className="num">{money(p.eligible_collateral_amount)}</td>
              <td className="num">{money(p.borrowing_base)}</td><td className="num">{money(p.drawn_amount)}</td><td className="num">{pct(p.utilization_pct)}</td>
              <td className="num">{pct(p.delinquency_30_pct)}</td><td className="num">{pct(p.delinquency_60_pct)}</td><td className="num">{pct(p.delinquency_90_pct)}</td>
              <td className="num">{pct(p.net_loss_rate_pct)}</td><td className="num">{pct(p.cpr_pct)}</td><td className="num">{pct(p.cdr_pct)}</td>
              <td className="num">{pct(p.weighted_avg_yield_pct)}</td><td className="num">{pct(p.excess_spread_pct)}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
      {series?.authorityBoundary && <div className="boundary">{series.authorityBoundary} A dash means the released tape did not carry the field the metric needs on at least 95% of its balance, or there is no prior tape to measure a period against.</div>}
    </Card>
  );
};

export default PoolTrendCard;
